import React, { useState, useEffect } from "react";
import { useCreateTestimonial, useUpdateTestimonial } from "./useTestimonials";
import { useForm } from "react-hook-form";
import Modal from "../../ui/Modal";
import FormRow from "../../ui/FormRow";
import { getImageUrl } from "../../helper/imageHelper";

const TestimonialsModal = ({ itemToEdit, onClose }) => {
  const { createTestimonial, isCreating } = useCreateTestimonial();
  const { updateTestimonial, isUpdating } = useUpdateTestimonial();
  const isEditing = !!itemToEdit;
  const isWorking = isCreating || isUpdating;

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: "",
      designation: "",
      review_text: "",
      rating: 5,
      active: true,
    }
  });

  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (itemToEdit) {
      reset({
        name: itemToEdit.name,
        designation: itemToEdit.designation,
        review_text: itemToEdit.review_text,
        rating: itemToEdit.rating,
        active: itemToEdit.active
      });
      setPreviewImage(getImageUrl(itemToEdit.image));
    }
  }, [itemToEdit, reset]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const onSubmit = (data) => {
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("designation", data.designation);
    formData.append("review_text", data.review_text);
    formData.append("rating", data.rating);
    formData.append("active", data.active ? 1 : 0);

    if (selectedFile) {
      formData.append("image", selectedFile);
    }

    if (isEditing) {
      updateTestimonial(
        { id: itemToEdit.id, formData },
        {
          onSuccess: () => {
            onClose();
          },
        }
      );
    } else {
      createTestimonial(formData, {
        onSuccess: () => {
          onClose();
        },
      });
    }
  };

  return (
    <Modal
      isLoading={isWorking}
      onClose={onClose}
      title={isEditing ? "Edit Testimonial" : "Add Testimonial"}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 font-primary text-white">
        {/* Image Upload */}
        <div className="flex flex-col items-center gap-4">
          <div className="relative h-24 w-24 overflow-hidden rounded-full border-4 border-white/10 bg-white/5 shadow-xl">
            {previewImage ? (
              <img src={previewImage} alt="Preview" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full items-center justify-center text-stone-400">
                No Img
              </div>
            )}
          </div>
          <label className="cursor-pointer rounded-lg bg-gradient-to-r from-white/10 to-white/5 px-4 py-2 text-sm font-medium text-stone-300 hover:from-white/20 hover:to-white/10 transition-all">
            Upload Image
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </label>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <FormRow lable="Name" error={errors.name?.message}>
            <input
              {...register("name", { required: "Name is required" })}
              className={`h-12 w-full rounded-lg border-transparent bg-transparent p-4 shadow-xl outline-none placeholder:text-stone-400 ${errors.name ? "border-2 focus-visible:border-red-500" : "border-none"}`}
              placeholder="John Doe"
            />
          </FormRow>

          <FormRow lable="Designation" error={errors.designation?.message}>
            <input
              {...register("designation", { required: "Designation is required" })}
              className={`h-12 w-full rounded-lg border-transparent bg-transparent p-4 shadow-xl outline-none placeholder:text-stone-400 ${errors.designation ? "border-2 focus-visible:border-red-500" : "border-none"}`}
              placeholder="CEO, Tech Corp"
            />
          </FormRow>
        </div>

        <FormRow lable="Rating">
          <select
            {...register("rating")}
            className="h-12 w-full rounded-lg border-none bg-black/20 p-4 shadow-xl outline-none text-stone-300"
          >
            <option value="5" className="bg-slate-800 text-stone-300">5 Stars</option>
            <option value="4" className="bg-slate-800 text-stone-300">4 Stars</option>
            <option value="3" className="bg-slate-800 text-stone-300">3 Stars</option>
            <option value="2" className="bg-slate-800 text-stone-300">2 Stars</option>
            <option value="1" className="bg-slate-800 text-stone-300">1 Star</option>
          </select>
        </FormRow>

        <FormRow lable="Review" error={errors.review_text?.message}>
          <textarea
            {...register("review_text", { required: "Review text is required" })}
            rows="4"
            className={`w-full rounded-lg border-transparent bg-transparent p-4 shadow-xl outline-none placeholder:text-stone-400 ${errors.review_text ? "border-2 focus-visible:border-red-500" : "border-none"}`}
            placeholder="Enter the testimonial text..."
          />
        </FormRow>

        {isEditing && (
          <div className="flex items-center gap-3 rounded-lg bg-white/5 p-4">
            <input
              type="checkbox"
              id="active"
              {...register("active")}
              className="h-5 w-5 rounded border-white/20 bg-white/10 text-primary focus:ring-primary"
            />
            <label htmlFor="active" className="text-sm font-medium text-stone-300 cursor-pointer">Active (Show on website)</label>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={isWorking}
            className="btn flex min-w-[8rem] items-center justify-center border-none bg-gradient-to-tr from-sky-600 to-cyan-400 py-3 font-semibold uppercase text-white shadow-lg hover:from-cyan-400 hover:to-sky-600 active:scale-95 disabled:opacity-50 transition-all rounded-lg"
          >
            {isWorking ? "Saving..." : "Save Testimonial"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default TestimonialsModal;
